import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { isSupabaseConfigured, supabase } from "@/services/supabase-client.service";
import { getCalendarResources } from "@/services/appointment.service";
import { getScheduleStaff } from "@/services/schedule.service";
import type { CalendarResource } from "@/types/appointment";
import type { ScheduleStaffMember } from "@/types/schedule";

// ---------------------------------------------------------------------------
// Query Key Hierarchien
// ---------------------------------------------------------------------------

export const doctorKeys = {
    all: ["doctors"] as const,
    list: (practiceId?: string) => [...doctorKeys.all, "list", practiceId] as const,
};

export const resourceKeys = {
    all: ["calendarResources"] as const,
    list: (practiceId?: string) => [...resourceKeys.all, "list", practiceId] as const,
};

export const scheduleStaffKeys = {
    all: ["scheduleStaff"] as const,
    list: (practiceId?: string) => [...scheduleStaffKeys.all, "list", practiceId] as const,
};

// ---------------------------------------------------------------------------
// Query Hooks
// ---------------------------------------------------------------------------

/**
 * Lädt Kalender-Ressourcen (Ärzte + Räume + Geräte) aus dem React Query Cache.
 * Wraps getCalendarResources() aus appointment.service.ts.
 * staleTime 5 min — Ressourcen sind innerhalb einer Session stabil.
 */
export function useCalendarResourcesQuery(practiceId?: string) {
    return useQuery<CalendarResource[]>({
        queryKey: resourceKeys.list(practiceId),
        queryFn: () => getCalendarResources(practiceId!),
        enabled: !!practiceId,
        staleTime: 1000 * 60 * 5,
    });
}

/**
 * Lädt echte Mitarbeiterdaten für den Dienstplan.
 * Wraps getScheduleStaff() aus schedule.service.ts (mit DEMO_STAFF-Fallback bei Fehler).
 */
export function useScheduleStaffQuery(practiceId?: string) {
    return useQuery<ScheduleStaffMember[]>({
        queryKey: scheduleStaffKeys.list(practiceId),
        queryFn: () => getScheduleStaff(practiceId!),
        enabled: !!practiceId,
        staleTime: 1000 * 60 * 5,
    });
}

// ---------------------------------------------------------------------------
// Realtime Hooks
// ---------------------------------------------------------------------------

/**
 * Abonniert Änderungen an der doctors-Tabelle.
 * Invalidiert doctorKeys.all UND resourceKeys.all, da getCalendarResources()
 * beide Tabellen in einem kombinierten Array zurückgibt.
 */
export function useDoctorsRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`doctors-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "doctors",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: doctorKeys.all });
                    queryClient.invalidateQueries({ queryKey: resourceKeys.all });
                    queryClient.invalidateQueries({ queryKey: scheduleStaffKeys.all });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}

/**
 * Abonniert Änderungen an der practice_resources-Tabelle (Räume, Geräte).
 * Invalidiert resourceKeys.all.
 */
export function useResourcesRealtime(practiceId?: string) {
    const queryClient = useQueryClient();

    useEffect(() => {
        if (!practiceId || !isSupabaseConfigured()) return;

        const channel = supabase
            .channel(`practice-resources-realtime-${practiceId}`)
            .on(
                "postgres_changes",
                {
                    event: "*",
                    schema: "public",
                    table: "practice_resources",
                    filter: `practice_id=eq.${practiceId}`,
                },
                () => {
                    queryClient.invalidateQueries({ queryKey: resourceKeys.all });
                },
            )
            .subscribe();

        return () => {
            void supabase.removeChannel(channel);
        };
    }, [practiceId, queryClient]);
}
